/* eslint-disable max-lines */

import {
  ClipItem,
  MyVisitsOutput,
  PlaceOtherReviewGroupsInput,
  PlaceOtherReviewGroupsOutput,
  ReviewGroupConnection,
  ThemelistsConnection,
  UserRelationshipsOutput,
  UserReviewGroupsConnection,
  UserReviewGroupsOutput,
  PaymentsOutput,
  PaymentsPageInfo,
  RecentVisitsWithoutReviewOutput,
  UserReviewFeedQueryVariables,
} from '@web/gql/graphql'
import { paymentsCachePolicy } from '@web/pages/payments/cachePolicy'

import { extractByTypename, isTypename } from './typeGuard'
import { StrictTypedTypePolicies } from './typePolicies.helper'
import unionBy from '../lodash/unionBy'

const typePolicies: StrictTypedTypePolicies = {
  Query: {
    fields: {
      feeds: {
        keyArgs: ['input', ['tab', 'feedType', 'regionCodes', 'regionCode', 'categoryCode']],
        merge: (existing, incoming) => {
          if (!isTypename(existing, 'Feeds_Succeed')) return incoming

          if (!isTypename(incoming, 'Feeds_Succeed')) return existing

          return {
            ...incoming,
            feeds: unionBy(existing.feeds, incoming.feeds, '__ref'),
          }
        },
      },
      userRelationships: {
        keyArgs: ['input', ['idno', 'type']],
        merge: (
          existing: UserRelationshipsOutput | undefined,
          incoming: UserRelationshipsOutput,
          { variables },
        ): UserRelationshipsOutput => {
          if (!existing) return incoming

          if (!variables?.after) return incoming

          if (!isTypename(existing, 'UserRelationshipsSucceed')) return incoming

          if (!isTypename(incoming, 'UserRelationshipsSucceed')) return existing

          return {
            ...incoming,
            connection: {
              ...incoming.connection,
              edges: [...existing.connection.edges, ...incoming.connection.edges],
            },
          }
        },
      },
      myVisits: {
        keyArgs: false,
        merge: (existing: MyVisitsOutput | undefined, incoming: MyVisitsOutput, { variables }): MyVisitsOutput => {
          if (!existing) return incoming

          if (!variables?.input?.after) return incoming

          if (!isTypename(existing, 'MyVisitsSucceed')) return incoming

          if (!isTypename(incoming, 'MyVisitsSucceed')) return existing

          return {
            ...incoming,
            connection: {
              ...incoming.connection,
              edges: [...existing.connection.edges, ...incoming.connection.edges],
            },
          }
        },
      },
      recentVisitsWithoutReview: {
        keyArgs: ['input', ['recentDays']],
        merge: (
          existing: RecentVisitsWithoutReviewOutput | undefined,
          incoming: RecentVisitsWithoutReviewOutput,
        ): RecentVisitsWithoutReviewOutput => {
          if (!existing) return incoming

          if (!incoming) return existing

          if (!isTypename(existing, 'RecentVisitsWithoutReview_Succeed')) return incoming

          if (!isTypename(incoming, 'RecentVisitsWithoutReview_Succeed'))
            return {
              ...existing,
              pageInfo: { hasNextPage: false, endCursor: null },
            }

          return {
            ...incoming,
            visits: [...existing.visits, ...incoming.visits],
          }
        },
      },
      visitHistories: {
        keyArgs: ['$visitId', 'visitHistoriesInput', ['placeId']],
        merge: (existing, incoming) => {
          if (!existing) return incoming

          if (!incoming) return existing

          return unionBy(existing, incoming, '__ref')
        },
      },
      payments: {
        keyArgs: ['input', ['cardIds', 'includeOnSiteCard', 'includeOnSitePointMoney']],
        merge: (existing: PaymentsOutput, incoming: PaymentsOutput, { mergeObjects }): PaymentsOutput => {
          if (!existing) return incoming

          if (incoming.__typename === 'PaySession_Failed') return incoming

          if (incoming.__typename !== 'Payments_Succeed') return existing

          return mergeObjects(existing, incoming)
        },
      },
      placeOtherReviewGroups: {
        keyArgs: [
          'input',
          [
            'placeId',
            'excludeReviewGroupId',
            'sort',
            'mediaFilter',
            'contentFilter',
          ] as (keyof PlaceOtherReviewGroupsInput)[],
        ],
        merge: (
          existing: PlaceOtherReviewGroupsOutput | undefined,
          incoming: PlaceOtherReviewGroupsOutput,
          { variables },
        ): PlaceOtherReviewGroupsOutput => {
          if (!existing) return incoming

          if (!variables?.input?.after) return incoming

          if (!isTypename(existing, 'ReviewGroupConnection')) return incoming

          if (!isTypename(incoming, 'ReviewGroupConnection')) return existing

          const result = {
            ...incoming,
            edges: [...existing.edges, ...incoming.edges],
          } as ReviewGroupConnection

          return result
        },
      },
      userThemelists: {
        keyArgs: ['input', ['userIdno']],
        merge: (existing, incoming, { variables }): ThemelistsConnection => {
          if (!existing) return incoming

          if (!variables?.connection?.after) return incoming

          if (!incoming) return existing

          return {
            ...incoming,
            edges: [...existing.edges, ...incoming.edges],
          }
        },
      },
      userReviewGroups: {
        keyArgs: [
          'input',
          ['userIdno', 'textFilter', 'mediaFilter', 'placeCategoryId', 'regionCode', 'sort', 'dateTimeFilter'],
          '@connection',
          ['key'],
        ],
        merge: (existing, incoming: UserReviewGroupsOutput) => {
          if (!existing) return incoming

          if (!incoming) return existing

          const { UserReviewGroupsConnection } = extractByTypename(incoming)
          if (!UserReviewGroupsConnection) return existing

          return { ...existing, ...incoming }
        },
      },
      userReviewGroupCategories: {
        keyArgs: ['input', ['userIdno', 'mediaFilter']],
        merge: false,
      },
      reviewGroupReactions: {
        keyArgs: ['input', ['reviewGroupId', 'sort', 'reactionType']],
        merge: (existing, incoming, { variables }) => {
          if (!existing) return incoming

          if (!variables?.input?.after) return incoming

          if (!incoming) return existing

          return {
            ...incoming,
            edges: [...existing.edges, ...incoming.edges],
          }
        },
      },
      userReactions: {
        keyArgs: ['input', ['sort', 'reactionType']],
        merge: (existing, incoming) => {
          if (!existing) return incoming

          if (!incoming) return existing
          return {
            ...incoming,
            edges: [...existing.edges, ...incoming.edges],
          }
        },
      },
      // TODO: 임시로 작업
      notificationMissionGroups: {
        keyArgs: ['input', ['reviewGroupId']],
        merge: (existing, incoming) => {
          if (existing) return existing

          return incoming
        },
      },
      selectableProofsForReviewWriteByReviewGroup: {
        keyArgs: ['reviewGroupId'],
      },
      selectableProofsForReviewWriteByVisit: {
        keyArgs: ['visitId'],
      },
      userClips: {
        keyArgs: ['input', ['channelId']],
        merge: (existing, incoming): ClipItem[] => {
          if (!existing) return incoming

          if (!incoming) return existing

          return [...existing, ...incoming]
        },
      },
    },
  },
  UserReviewGroupsConnection: {
    fields: {
      edges: {
        read: (existing: UserReviewGroupsConnection['edges'], { variables, readField }) => {
          const targetReviewGroupId = variables?.connection?.reviewGroupId
          if (!targetReviewGroupId) return existing

          const hasTargetReviewGroup = existing?.some(
            reviewGroup => readField('id', reviewGroup) === targetReviewGroupId,
          )
          return hasTargetReviewGroup ? existing : undefined
        },
        merge: (existing, incoming, { variables }) => {
          if (!existing) return incoming

          if (!incoming) return existing

          if ((variables as UserReviewFeedQueryVariables)?.connection.before) {
            return unionBy(incoming, existing, '__ref')
          }

          if ((variables as UserReviewFeedQueryVariables)?.connection.after) {
            return unionBy(existing, incoming, '__ref')
          }

          return incoming
        },
      },
      pageInfo: {
        merge: (existing, incoming, { variables }) => {
          if (!existing) return incoming

          if (!incoming) return existing

          if ((variables as UserReviewFeedQueryVariables)?.connection.before) {
            return {
              ...existing,
              hasPreviousPage: incoming.hasPreviousPage,
              startCursor: incoming.startCursor,
            }
          }

          if ((variables as UserReviewFeedQueryVariables)?.connection.after) {
            return {
              ...existing,
              hasNextPage: incoming.hasNextPage,
              endCursor: incoming.endCursor,
            }
          }

          return { ...existing, ...incoming }
        },
      },
    },
  },
  Themelist: {
    keyFields: ['id'],
    fields: {
      statistic: {
        merge: true,
      },
      reviewGroups: {
        keyArgs: ['id'],
        merge: (existing, incoming, options) => {
          if (!options.args) return incoming

          if (!existing) return incoming

          if (!incoming) return existing

          if (options.args.page === 1) return incoming

          return [...existing, ...incoming]
        },
      },
    },
    merge: true,
  },
  User: {
    merge: true,
    fields: {
      profile: {
        merge: true,
      },
      stat: {
        merge: true,
      },
    },
  },
  CardPaymentAccess: {
    keyFields: [],
    merge: true,
  },
  PaymentJob: {
    keyFields: [],
    merge: true,
  },
  PaymentsPageInfo: {
    fields: {
      cursor: {
        merge: (existing, incoming) => {
          if (!existing) return incoming

          if (!incoming) return existing
          return incoming
        },
      },
    },
  },
  Payments_Succeed: {
    fields: {
      pageInfo: {
        keyArgs: ['sort', '$input', ['isReviewTargetFilter'], '$isUpdatedPayments'],
        merge: (existing: PaymentsPageInfo, incoming: PaymentsPageInfo) => {
          if (!existing) return incoming

          if (!incoming) return existing
          return incoming
        },
      },
      payments: paymentsCachePolicy?.fields?.payments,
    },
  },
  Place: {
    fields: {
      category: {
        merge: (existing, incoming) => {
          if (!existing) return incoming

          if (!incoming) return existing

          return incoming
        },
      },
      thumbnails: {
        merge: (existing, incoming) => {
          if (!existing) return incoming

          if (!incoming?.length) return existing

          return incoming
        },
      },
      coordinate: {
        merge: (existing, incoming) => {
          if (!existing) return incoming

          if (!incoming?.length) return existing

          return incoming
        },
      },
      jibunAddress: {
        merge: (existing, incoming) => {
          if (!existing) return incoming

          if (!incoming) return existing

          return incoming
        },
      },
      roadAddress: {
        merge: (existing, incoming) => {
          if (!existing) return incoming

          if (!incoming) return existing

          return incoming
        },
      },
    },
  },
  Keyword: {
    keyFields: ['code'],
    fields: {
      text: {
        merge: true,
      },
    },
  },
  FeverPlaceCategory: {
    keyFields: ['id', 'reviewGroupsCount'],
    fields: {
      reviewGroupsCount: {
        keyArgs: ['input', ['userIdno', 'mediaFilter']],
      },
    },
  },
  Decoration: {
    keyFields: ['name'],
  },
  ReactionStat: {
    fields: {
      reactionCounts: {
        merge: (existing, incoming) => {
          if (!existing) return incoming

          if (!incoming) return existing

          return incoming
        },
      },
    },
  },
  Visit: {
    merge: true,
    keyFields: ['id'],
    fields: {
      private: {
        merge: true,
      },
    },
  },
  MeSucceed: {
    merge: true,
  },
}

export default typePolicies
